/**
 * taxonomy.gql-map.ts — the PURE mapping layer between the taxonomy graph
 * shapes and the legacy REST types (CU-1b; masters.gql-map precedent).
 *
 * Deliberately import-free of the axios/i18n stack so the vitest matrix can
 * exercise every derivation in the plain node environment.
 *
 * Mapping stance:
 * - KEY PRESENCE IS PART OF THE CONTRACT (the CU-1a bank-wire lesson). The
 *   mock's seeded node is exactly nine keys — `netsuiteSegmentValueId` and
 *   the four enrich fields are ABSENT until something sets them, because the
 *   handler assigns `undefined` and `JSON.stringify` drops the key. The graph
 *   answers `null` / `[]` for the same state, so every optional field here is
 *   emitted ONLY when it carries a value. Emitting `null` instead would be a
 *   byte-diff on every row of the shadow leg — and, in the browser, would
 *   turn "never entered" into "explicitly empty".
 * - `nameEn`/`nameZh` are the legacy names for the SDL's `labelEn`/`labelZh`.
 * - `externalMappings` collapses the SDL's `[{system, code}]` list onto the
 *   FE's `{gs1?, google?}` capture-only object; an unknown `system` is
 *   DROPPED rather than guessed (capture-only, no live sync — plan Appendix C).
 * - IDS: the taxonomy graph exposes the raw `cat-<code>` id directly, so no
 *   id translation happens here at all.
 */

import type {
  GqlTaxonomyAuditEntry,
  GqlTaxonomyLevelConfig,
  GqlTaxonomyNode,
  GqlUserError,
} from './taxonomy.operations';
import type {
  CategoryAuditRow,
  CategoryDepth,
  CategoryExternalMappings,
  CategoryLevelConfig,
  ProductCategory,
} from '@/types/api/productCategory';

/**
 * The legacy REST refusal, rebuilt from a graph `userErrors` row.
 *
 * The taxonomy consumers read guard failures off `error.response.data.error`
 * (`extractGuardError` in `src/hooks/useTaxonomy.ts`). The graph reports the
 * SAME code vocabulary as `userErrors[].code` — every taxonomy guard code is
 * in the SDL's `UserErrorCode` enum verbatim — so the live leg rebuilds the
 * mock's exact axios-shaped refusal rather than asking consumers to learn a
 * second error shape. This is what keeps the flip invisible to the UI, and
 * what lets the L2 fe-seam gateway re-serve the legacy status codes.
 */
export class LegacyGuardError extends Error {
  readonly response: { status: number; data: { error: string; message: string } };

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = 'LegacyGuardError';
    this.response = { status, data: { error: code, message } };
  }
}

/**
 * Guard code → legacy HTTP status. VERBATIM from the mock handler's
 * `GUARD_STATUS` map (mocks/handlers/productCategory.ts) — the handler is the
 * spec, so this table is a copy of it, not a re-derivation. Anything absent
 * is 400, the handler's own default.
 */
export const TAXONOMY_GUARD_STATUS: Readonly<Record<string, number>> = {
  PARENT_NOT_FOUND: 404,
  NODE_NOT_FOUND: 404,
  CODE_TAKEN: 409,
  REPLACEMENT_REQUIRED: 409,
};

/** The mock's refusal message template, verbatim. */
export function taxonomyGuardMessage(code: string): string {
  return code === 'COMMENT_REQUIRED'
    ? 'Change comment is required'
    : `Category rule violated: ${code}`;
}

/**
 * Turn a non-empty `userErrors` list into the legacy refusal the mock emits.
 * The FIRST row wins — the mock refuses on the first violated guard too.
 */
export function throwTaxonomyGuard(userErrors: readonly GqlUserError[]): void {
  if (userErrors.length === 0) return;
  const first = userErrors[0];
  throw new LegacyGuardError(
    first.code,
    taxonomyGuardMessage(first.code),
    TAXONOMY_GUARD_STATUS[first.code] ?? 400
  );
}

/** The mock's NODE_NOT_FOUND refusal, for a graph read that answered null. */
export function throwNodeNotFound(): never {
  throw new LegacyGuardError('NODE_NOT_FOUND', taxonomyGuardMessage('NODE_NOT_FOUND'), 404);
}

/**
 * Collapse the SDL mapping list onto the FE capture-only object. Returns
 * `undefined` (⇒ the key is omitted) when nothing recognized is present.
 */
export function mapExternalMappings(
  rows: ReadonlyArray<{ system: string; code: string }>
): CategoryExternalMappings | undefined {
  const out: CategoryExternalMappings = {};
  for (const row of rows) {
    if (row.system === 'gs1') out.gs1 = row.code;
    else if (row.system === 'google') out.google = row.code;
  }
  return out.gs1 === undefined && out.google === undefined ? undefined : out;
}

/** The inverse — the write direction, dropping absent members. */
export function toExternalMappingInput(
  mappings: CategoryExternalMappings | undefined
): Array<{ system: string; code: string }> | undefined {
  if (!mappings) return undefined;
  const rows: Array<{ system: string; code: string }> = [];
  if (mappings.gs1 !== undefined) rows.push({ system: 'gs1', code: mappings.gs1 });
  if (mappings.google !== undefined) rows.push({ system: 'google', code: mappings.google });
  return rows;
}

/**
 * One graph node → one legacy `ProductCategory`. Optional fields are added
 * ONLY when they carry a value (see the key-presence note in the header).
 */
export function mapTaxonomyNode(node: GqlTaxonomyNode): ProductCategory {
  const mapped: ProductCategory = {
    id: node.id,
    code: node.code,
    parentId: node.parentId,
    depth: node.depth as CategoryDepth,
    path: node.path,
    sortOrder: node.sortOrder,
    nameEn: node.labelEn,
    nameZh: node.labelZh,
    isActive: node.isActive,
  };
  if (node.netsuiteSegmentValueId != null) {
    mapped.netsuiteSegmentValueId = node.netsuiteSegmentValueId;
  }
  if (node.description != null) mapped.description = node.description;
  if (node.iconKey != null) mapped.iconKey = node.iconKey;
  if (node.synonyms.length > 0) mapped.synonyms = node.synonyms;
  const externalMappings = mapExternalMappings(node.externalMappings);
  if (externalMappings) mapped.externalMappings = externalMappings;
  return mapped;
}

/** Depth-first flatten of the tree read — parents precede their children. */
export function flattenTaxonomyTree(roots: readonly GqlTaxonomyNode[]): ProductCategory[] {
  const out: ProductCategory[] = [];
  const walk = (nodes: readonly GqlTaxonomyNode[]): void => {
    for (const node of nodes) {
      out.push(mapTaxonomyNode(node));
      if (node.children && node.children.length > 0) walk(node.children);
    }
  };
  walk(roots);
  return out;
}

/**
 * The handler's `sortForRead` contract, VERBATIM: path ascending, then
 * sortOrder. Applied on the live leg too so both worlds emit one row order.
 */
export function sortForRead(nodes: readonly ProductCategory[]): ProductCategory[] {
  return [...nodes].sort((a, b) => a.path.localeCompare(b.path) || a.sortOrder - b.sortOrder);
}

export function mapLevelConfig(level: GqlTaxonomyLevelConfig): CategoryLevelConfig {
  return {
    level: level.depth as CategoryDepth,
    labelEn: level.labelEn,
    labelZh: level.labelZh,
    enabled: level.enabled,
  };
}

/**
 * Audit rows. `before`/`after` are an HONEST GAP: the graph entry carries the
 * action/target/comment triple the lens renders, and the server does not
 * expose the value snapshots. Omitting the keys (rather than emitting `{}`)
 * keeps "not served" distinguishable from "changed nothing".
 */
export function mapAuditEntry(entry: GqlTaxonomyAuditEntry): CategoryAuditRow {
  return {
    id: entry.id,
    at: entry.at,
    action: entry.action as CategoryAuditRow['action'],
    targetId: entry.targetId,
    comment: entry.comment,
  };
}
